// src/components/PullRequestReview.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Snackbar,
  Alert,
  useTheme,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Badge
} from '@mui/material';
import { 
  ArrowForward as ArrowForwardIcon, 
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DiffEditor } from '@monaco-editor/react';
import Layout from './Layout';
import CreatePullRequest from './CreatePullRequest';

const PullRequestReview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId, issueKey } = useParams();
  const theme = useTheme();
  
  const { originalCode, fixedCode, fileName } = location.state || {};
  
  const [mergedCode, setMergedCode] = useState('');
  const [individualChanges, setIndividualChanges] = useState([]);
  const [acceptedChanges, setAcceptedChanges] = useState(new Set());
  const [rejectedChanges, setRejectedChanges] = useState(new Set());
  const [changeStatuses, setChangeStatuses] = useState(new Map());
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const token = localStorage.getItem("access_token");

  // Parse diff and extract individual changes
  const parseDiffChanges = useCallback((original, fixed) => {
    try {
      if (!original || !fixed) return [];
      
      const originalLines = original.split('\n');
      const fixedLines = fixed.split('\n');
      const changes = [];
      let changeId = 0;

      // Simple diff algorithm to identify changes
      let i = 0, j = 0;
      while (i < originalLines.length || j < fixedLines.length) {
        if (i >= originalLines.length) {
          // Remaining lines are additions
          while (j < fixedLines.length) {
            changes.push({
              id: changeId++,
              type: 'addition',
              originalLineNumber: null,
              modifiedLineNumber: j + 1,
              originalContent: null,
              modifiedContent: fixedLines[j],
              status: 'pending' // pending, accepted, rejected
            });
            j++;
          }
          break;
        }
        
        if (j >= fixedLines.length) {
          // Remaining lines are deletions
          while (i < originalLines.length) {
            changes.push({
              id: changeId++,
              type: 'deletion',
              originalLineNumber: i + 1,
              modifiedLineNumber: null,
              originalContent: originalLines[i],
              modifiedContent: null,
              status: 'pending'
            });
            i++;
          }
          break;
        }

        if (originalLines[i] === fixedLines[j]) {
          // Lines are the same, move both pointers
          i++;
          j++;
        } else {
          // Found a difference
          // Check if it's a modification or insertion/deletion
          let foundMatch = false;
          
          // Look ahead to see if this is a modification or insertion/deletion
          for (let k = j + 1; k < Math.min(j + 5, fixedLines.length); k++) {
            if (originalLines[i] === fixedLines[k]) {
              // Found insertion(s)
              for (let l = j; l < k; l++) {
                changes.push({
                  id: changeId++,
                  type: 'addition',
                  originalLineNumber: null,
                  modifiedLineNumber: l + 1,
                  originalContent: null,
                  modifiedContent: fixedLines[l],
                  status: 'pending'
                });
              }
              j = k;
              foundMatch = true;
              break;
            }
          }

          if (!foundMatch) {
            for (let k = i + 1; k < Math.min(i + 5, originalLines.length); k++) {
              if (originalLines[k] === fixedLines[j]) {
                // Found deletion(s)
                for (let l = i; l < k; l++) {
                  changes.push({
                    id: changeId++,
                    type: 'deletion',
                    originalLineNumber: l + 1,
                    modifiedLineNumber: null,
                    originalContent: originalLines[l],
                    modifiedContent: null,
                    status: 'pending'
                  });
                }
                i = k;
                foundMatch = true;
                break;
              }
            }
          }

          if (!foundMatch) {
            // This is a modification
            changes.push({
              id: changeId++,
              type: 'modification',
              originalLineNumber: i + 1,
              modifiedLineNumber: j + 1,
              originalContent: originalLines[i],
              modifiedContent: fixedLines[j],
              status: 'pending'
            });
            i++;
            j++;
          }
        }
      }

      return changes;
    } catch (error) {
      console.error('Error parsing diff changes:', error);
      return [];
    }
  }, []);

  // Extract original code from the issue details structure
  const getOriginalCodeString = useCallback(() => {
    try {
      if (!originalCode) return '';
      
      if (typeof originalCode === 'string') {
        return originalCode;
      }
      
      if (!Array.isArray(originalCode)) return '';
      
      return originalCode.map(issue => {
        const codeLines = issue.code_snippet?.code_lines || [];
        return codeLines.map(line => line.code || '').join('\n');
      }).join('\n');
    } catch (error) {
      console.error('Error getting original code string:', error);
      return '';
    }
  }, [originalCode]);

  useEffect(() => {
    try {
      if (fixedCode && originalCode) {
        const originalCodeString = getOriginalCodeString();
        setMergedCode(originalCodeString);
        
        // Parse individual changes
        const changes = parseDiffChanges(originalCodeString, fixedCode);
        setIndividualChanges(changes);
        
        // Initialize change statuses
        const initialStatuses = new Map();
        changes.forEach(change => {
          initialStatuses.set(change.id, 'pending');
        });
        setChangeStatuses(initialStatuses);
      }
    } catch (error) {
      console.error('Error in useEffect:', error);
    }
  }, [fixedCode, originalCode, parseDiffChanges, getOriginalCodeString]);

  // Apply changes based on acceptance/rejection status
  const applyChangesToMergedCode = useCallback(() => {
    try {
      if (!originalCode || individualChanges.length === 0) return;

      const originalCodeString = getOriginalCodeString();
      const originalLines = originalCodeString.split('\n');
      const result = [...originalLines];
      
      // Sort changes by line number (descending for modifications/deletions, ascending for additions)
      const sortedChanges = [...individualChanges].sort((a, b) => {
        const lineA = a.originalLineNumber || a.modifiedLineNumber || 0;
        const lineB = b.originalLineNumber || b.modifiedLineNumber || 0;
        return lineB - lineA;
      });

      sortedChanges.forEach(change => {
        const status = changeStatuses.get(change.id);
        
        if (status === 'accepted') {
          if (change.type === 'addition') {
            // Insert the new line
            const insertIndex = change.modifiedLineNumber - 1;
            if (insertIndex >= 0) {
              result.splice(insertIndex, 0, change.modifiedContent);
            }
          } else if (change.type === 'deletion') {
            // Remove the line
            const deleteIndex = change.originalLineNumber - 1;
            if (deleteIndex >= 0 && deleteIndex < result.length) {
              result.splice(deleteIndex, 1);
            }
          } else if (change.type === 'modification') {
            // Replace the line
            const modifyIndex = change.originalLineNumber - 1;
            if (modifyIndex >= 0 && modifyIndex < result.length) {
              result[modifyIndex] = change.modifiedContent;
            }
          }
        }
        // If rejected or pending, keep original content (do nothing)
      });

      setMergedCode(result.join('\n'));
    } catch (error) {
      console.error('Error applying changes to merged code:', error);
    }
  }, [individualChanges, changeStatuses, originalCode, getOriginalCodeString]);

  useEffect(() => {
    applyChangesToMergedCode();
  }, [changeStatuses, applyChangesToMergedCode]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handlePullRequestSuccess = useCallback((data) => {
    try {
      setSnackbar({
        open: true,
        message: 'Pull Request created successfully!',
        severity: 'success'
      });
      
      // Increase timeout to allow mark-issue-solved API to complete
      setTimeout(() => {
        if (navigate && projectId) {
          navigate(`/projects/${projectId}`, { state: { activeTab: 2 } });
        }
      }, 1000); // Increased from 2000ms to 3000ms
    } catch (error) {
      console.error('Error in handlePullRequestSuccess:', error);
    }
  }, [navigate, projectId]);

  const handlePullRequestError = useCallback((error) => {
    try {
      setSnackbar({
        open: true,
        message: `Failed to create Pull Request: ${error}`,
        severity: 'error'
      });
    } catch (err) {
      console.error('Error in handlePullRequestError:', err);
    }
  }, []);

  const handleBackToIssue = useCallback(() => {
    try {
        if (navigate && projectId && issueKey) {
          navigate(`/issues/${projectId}/${issueKey}`);
        }
      } catch (error) {
        console.error('Error in handleBackToIssue:', error);
      }
  }, [navigate, projectId, issueKey]);

  // Handle individual change acceptance/rejection
  const handleChangeAction = useCallback((changeId, action) => {
    try {
      setChangeStatuses(prev => {
        const newStatuses = new Map(prev);
        const currentStatus = newStatuses.get(changeId);
        
        if (action === 'accept') {
          newStatuses.set(changeId, currentStatus === 'accepted' ? 'pending' : 'accepted');
        } else if (action === 'reject') {
          newStatuses.set(changeId, currentStatus === 'rejected' ? 'pending' : 'rejected');
        }
        
        return newStatuses;
      });

      // Update accepted/rejected sets for backward compatibility
      setAcceptedChanges(prev => {
        const newSet = new Set(prev);
        if (action === 'accept') {
          const currentStatus = changeStatuses.get(changeId);
          if (currentStatus === 'accepted') {
            newSet.delete(changeId);
          } else {
            newSet.add(changeId);
          }
        }
        return newSet;
      });

      setRejectedChanges(prev => {
        const newSet = new Set(prev);
        if (action === 'reject') {
          const currentStatus = changeStatuses.get(changeId);
          if (currentStatus === 'rejected') {
            newSet.delete(changeId);
          } else {
            newSet.add(changeId);
          }
        }
        return newSet;
      });
    } catch (error) {
      console.error('Error in handleChangeAction:', error);
    }
  }, [changeStatuses]);

  const handleAcceptAllChanges = useCallback(() => {
    try {
      const newStatuses = new Map();
      individualChanges.forEach(change => {
        newStatuses.set(change.id, 'accepted');
      });
      setChangeStatuses(newStatuses);
      setAcceptedChanges(new Set(individualChanges.map(c => c.id)));
      setRejectedChanges(new Set());
    } catch (error) {
      console.error('Error in handleAcceptAllChanges:', error);
    }
  }, [individualChanges]);

  const handleRejectAllChanges = useCallback(() => {
    try {
      const newStatuses = new Map();
      individualChanges.forEach(change => {
        newStatuses.set(change.id, 'rejected');
      });
      setChangeStatuses(newStatuses);
      setAcceptedChanges(new Set());
      setRejectedChanges(new Set(individualChanges.map(c => c.id)));
    } catch (error) {
      console.error('Error in handleRejectAllChanges:', error);
    }
  }, [individualChanges]);

  const getChangeIcon = (type) => {
    switch (type) {
      case 'addition':
        return <AddIcon sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'deletion':
        return <RemoveIcon sx={{ fontSize: 16, color: 'error.main' }} />;
      case 'modification':
        return <EditIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
      default:
        return null;
    }
  };

  const getChangeColor = (type) => {
    switch (type) {
      case 'addition':
        return 'success';
      case 'deletion':
        return 'error';
      case 'modification':
        return 'warning';
      default:
        return 'default';
    }
  };

  const acceptedCount = Array.from(changeStatuses.values()).filter(status => status === 'accepted').length;
  const rejectedCount = Array.from(changeStatuses.values()).filter(status => status === 'rejected').length;
  const pendingCount = individualChanges.length - acceptedCount - rejectedCount;

  if (!originalCode || !fixedCode) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" color="error">
              No code data available for review.
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleBackToIssue}
              sx={{ mt: 2 }}
            >
              Back to Issue
            </Button>
          </Paper>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Code Review & Pull Request
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Chip 
                label={fileName || 'Unknown File'} 
                variant="outlined" 
                color="primary" 
              />
              <Typography variant="body2" color="text.secondary">
                Project: {projectId} • Issue: {issueKey}
              </Typography>
            </Box>

            {/* Change Summary Chips */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip 
                icon={<CheckCircleIcon />}
                label={`${acceptedCount} Accepted`}
                color="success"
                variant={acceptedCount > 0 ? "filled" : "outlined"}
                size="small"
              />
              <Chip 
                icon={<CloseIcon />}
                label={`${rejectedCount} Rejected`}
                color="error"
                variant={rejectedCount > 0 ? "filled" : "outlined"}
                size="small"
              />
              <Chip 
                label={`${pendingCount} Pending`}
                color="default"
                variant={pendingCount > 0 ? "filled" : "outlined"}
                size="small"
              />
            </Box>

            <Divider />
          </Box>

          {/* Individual Changes List */}
          {individualChanges.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Individual Changes ({individualChanges.length})
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    onClick={handleAcceptAllChanges}
                  >
                    Accept All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={handleRejectAllChanges}
                  >
                    Reject All
                  </Button>
                </Box>
              </Box>

              {/* Changes List */}
              <Box sx={{ 
                maxHeight: '300px', 
                overflowY: 'auto',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? theme.palette.grey[900] 
                  : theme.palette.grey[50]
              }}>
                {individualChanges.map((change) => {
                  const status = changeStatuses.get(change.id);
                  const isAccepted = status === 'accepted';
                  const isRejected = status === 'rejected';
                  
                  return (
                    <Box
                      key={change.id}
                      sx={{
                        p: 2,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        backgroundColor: isAccepted 
                          ? theme.palette.success.main + '10'
                          : isRejected 
                          ? theme.palette.error.main + '10'
                          : 'transparent',
                        '&:last-child': {
                          borderBottom: 'none'
                        }
                      }}
                    >
                      {/* Change Type Icon */}
                      <Box sx={{ minWidth: 24 }}>
                        {getChangeIcon(change.type)}
                      </Box>

                      {/* Change Details */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          {change.type === 'addition' && `Line ${change.modifiedLineNumber}: Addition`}
                          {change.type === 'deletion' && `Line ${change.originalLineNumber}: Deletion`}
                          {change.type === 'modification' && `Line ${change.originalLineNumber}: Modification`}
                        </Typography>
                        
                        {change.type === 'deletion' && change.originalContent && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace',
                              color: 'error.main',
                              backgroundColor: theme.palette.error.main + '15',
                              p: 0.5,
                              borderRadius: 0.5,
                              fontSize: '0.75rem'
                            }}
                          >
                            - {change.originalContent}
                          </Typography>
                        )}
                        
                        {change.type === 'addition' && change.modifiedContent && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace',
                              color: 'success.main',
                              backgroundColor: theme.palette.success.main + '15',
                              p: 0.5,
                              borderRadius: 0.5,
                              fontSize: '0.75rem'
                            }}
                          >
                            + {change.modifiedContent}
                          </Typography>
                        )}
                        
                        {change.type === 'modification' && (
                          <Box>
                            {change.originalContent && (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontFamily: 'monospace',
                                  color: 'error.main',
                                  backgroundColor: theme.palette.error.main + '15',
                                  p: 0.5,
                                  borderRadius: 0.5,
                                  fontSize: '0.75rem',
                                  mb: 0.5
                                }}
                              >
                                - {change.originalContent}
                              </Typography>
                            )}
                            {change.modifiedContent && (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontFamily: 'monospace',
                                  color: 'success.main',
                                  backgroundColor: theme.palette.success.main + '15',
                                  p: 0.5,
                                  borderRadius: 0.5,
                                  fontSize: '0.75rem'
                                }}
                              >
                                + {change.modifiedContent}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>

                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title={isAccepted ? "Undo accept" : "Accept change"}>
                          <IconButton
                            size="small"
                            onClick={() => handleChangeAction(change.id, 'accept')}
                            sx={{
                              backgroundColor: isAccepted ? theme.palette.success.main : 'transparent',
                              color: isAccepted ? 'white' : theme.palette.success.main,
                              '&:hover': {
                                backgroundColor: theme.palette.success.main + '20',
                              },
                              border: `1px solid ${theme.palette.success.main}`,
                            }}
                          >
                            <CheckCircleIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title={isRejected ? "Undo reject" : "Reject change"}>
                          <IconButton
                            size="small"
                            onClick={() => handleChangeAction(change.id, 'reject')}
                            sx={{
                              backgroundColor: isRejected ? theme.palette.error.main : 'transparent',
                              color: isRejected ? 'white' : theme.palette.error.main,
                              '&:hover': {
                                backgroundColor: theme.palette.error.main + '20',
                              },
                              border: `1px solid ${theme.palette.error.main}`,
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Code Diff Viewer */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Code Diff Preview
            </Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr 1fr', 
              gap: 2,
              height: '500px'
            }}>
              {/* Original Code */}
              <Box sx={{ 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                overflow: 'hidden'
              }}>
                <Box sx={{ 
                  p: 1, 
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? theme.palette.grey[800] 
                    : theme.palette.grey[100],
                  textAlign: 'center'
                }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Original Code
                  </Typography>
                </Box>
                <DiffEditor
                  height="460px"
                  language="javascript"
                  original={getOriginalCodeString()}
                  modified={getOriginalCodeString()}
                  keepCurrentOriginalModel={true} 
                  keepCurrentModifiedModel={true}
                  theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'light'}
                  options={{
                    readOnly: true,
                    renderSideBySide: false,
                    enableSplitViewResizing: false,
                    renderOverviewRuler: false,
                    diffCodeLens: false,
                    originalEditable: false,
                    modifiedEditable: false
                  }}
                />
              </Box>

              {/* AI Suggested Changes */}
              <Box sx={{ 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                overflow: 'hidden'
              }}>
                <Box sx={{ 
                  p: 1, 
                  backgroundColor: theme.palette.mode === 'dark'
                    ? theme.palette.info.dark
                    : theme.palette.info.light,
                  textAlign: 'center'
                }}>
                  <Typography 
                    variant="subtitle2" 
                    fontWeight="bold"
                    sx={{ color: theme.palette.info.contrastText }}
                  >
                    AI Suggested Changes
                  </Typography>
                </Box>
                <DiffEditor
                  height="460px"
                  language="javascript"
                  original={getOriginalCodeString()}
                  modified={fixedCode}
                  keepCurrentOriginalModel={true} 
                  keepCurrentModifiedModel={true}
                  theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'light'}
                  options={{
                    readOnly: true,
                    renderSideBySide: false,
                    enableSplitViewResizing: false,
                    renderOverviewRuler: true,
                    diffCodeLens: true,
                    originalEditable: false,
                    modifiedEditable: false
                  }}
                />
              </Box>

              {/* Merged Result */}
              <Box sx={{ 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                overflow: 'hidden'
              }}>
                <Box sx={{ 
                  p: 1, 
                  backgroundColor: theme.palette.mode === 'dark'
                    ? theme.palette.success.dark
                    : theme.palette.success.light,
                  textAlign: 'center'
                }}>
                  <Typography 
                    variant="subtitle2" 
                    fontWeight="bold"
                    sx={{ color: theme.palette.success.contrastText }}
                  >
                    Merged Result (For PR)
                  </Typography>
                </Box>
                <DiffEditor
                  height="460px"
                  language="javascript"
                  original={getOriginalCodeString()}
                  modified={mergedCode}
                  keepCurrentOriginalModel={true} 
                  keepCurrentModifiedModel={true}
                  theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'light'}
                  options={{
                    readOnly: false,
                    renderSideBySide: false,
                    enableSplitViewResizing: false,
                    renderOverviewRuler: true,
                    diffCodeLens: false,
                    originalEditable: false,
                    modifiedEditable: true
                  }}
                  onMount={(editor) => {
                    try {
                      if (editor && editor.getModifiedEditor) {
                        editor.getModifiedEditor().onDidChangeModelContent(() => {
                          const value = editor.getModifiedEditor().getValue();
                          setMergedCode(value);
                        });
                      }
                    } catch (error) {
                      console.error('Error setting up Monaco editor:', error);
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant="outlined"
              onClick={handleBackToIssue}
              disabled={!projectId || !issueKey}
            >
              Back to Issue
            </Button>

            <CreatePullRequest
                token={token}
                onSuccess={handlePullRequestSuccess}
                onError={handlePullRequestError}
                mergedCode={mergedCode} 
                acceptedChanges={acceptedChanges}
                issueKey={issueKey} 
                projectId={projectId}
                fileName={fileName}
            />
          </Box>
        </Paper>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default PullRequestReview;